import { useEffect, useState } from 'react';
import {
  MousePointer2,
  Lasso,
  Square,
  Circle,
  Trash2,
  Plus,
  Minus,
  RefreshCw,
  Hexagon,
  MousePointerClick,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@ooh/ui/components/tooltip';
import { Button } from '@ooh/ui/components/button';
import type { DrawingTool } from '@ooh/ui/components/map-view';
import { getToolSubMode } from '../lib/drawing-tool';
import type { ToolSubMode } from '../lib/drawing-tool';

type ToolGroup = 'lasso' | 'rectangle' | 'circle' | 'polygon' | 'point';

export interface DrawingToolPaletteProps {
  activeTool: DrawingTool;
  hasSelection: boolean;
  onToolChange: (tool: DrawingTool) => void;
  onClearShapes: () => void;
  t: (key: string) => string;
}

function getGroup(tool: DrawingTool): ToolGroup | null {
  if (tool.startsWith('lasso')) return 'lasso';
  if (tool.startsWith('rectangle')) return 'rectangle';
  if (tool.startsWith('circle')) return 'circle';
  if (tool.startsWith('polygon')) return 'polygon';
  if (tool.startsWith('point')) return 'point';
  return null;
}

function getMode(tool: DrawingTool): ToolSubMode {
  return getToolSubMode(tool);
}

function makeTool(group: ToolGroup, mode: ToolSubMode): DrawingTool {
  return `${group}-${mode}` as DrawingTool;
}

// icon-sm button = h-7 (28px), gap-1 = 4px between items, p-1 = 4px card padding
const BUTTON_H = 28;
const GAP = 4;
const CARD_PAD = 4;
const subMenuTopPx = (rowIndex: number) => CARD_PAD + rowIndex * (BUTTON_H + GAP);

const SUB_MODES: { mode: ToolSubMode; icon: React.ReactNode; labelKey: string }[] = [
  { mode: 'add', icon: <Plus className="h-3.5 w-3.5" />, labelKey: 'areaSelection.tools.modeAdd' },
  {
    mode: 'remove',
    icon: <Minus className="h-3.5 w-3.5" />,
    labelKey: 'areaSelection.tools.modeRemove',
  },
  {
    mode: 'fresh',
    icon: <RefreshCw className="h-3.5 w-3.5" />,
    labelKey: 'areaSelection.tools.modeFresh',
  },
];

const EXPANDABLE_TOOLS: {
  group: ToolGroup;
  icon: React.ReactNode;
  labelKey: string;
  shortcutKey: string;
  /** When present, only these sub-modes are shown in the sub-menu. All three shown if absent. */
  supportedModes?: ToolSubMode[];
}[] = [
  {
    group: 'lasso',
    icon: <Lasso className="h-4 w-4" />,
    labelKey: 'areaSelection.tools.lasso',
    shortcutKey: 'areaSelection.tools.shortcut.lasso',
  },
  {
    group: 'rectangle',
    icon: <Square className="h-4 w-4" />,
    labelKey: 'areaSelection.tools.rectangle',
    shortcutKey: 'areaSelection.tools.shortcut.rectangle',
  },
  {
    group: 'circle',
    icon: <Circle className="h-4 w-4" />,
    labelKey: 'areaSelection.tools.circle',
    shortcutKey: 'areaSelection.tools.shortcut.circle',
  },
  {
    group: 'polygon',
    icon: <Hexagon className="h-4 w-4" />,
    labelKey: 'areaSelection.tools.polygon',
    shortcutKey: 'areaSelection.tools.shortcut.polygon',
  },
  {
    group: 'point',
    icon: <MousePointerClick className="h-4 w-4" />,
    labelKey: 'areaSelection.tools.pointSelect',
    shortcutKey: 'areaSelection.tools.shortcut.pointSelect',
    // No ↺ (fresh) for single-click selection — replace makes no sense
    supportedModes: ['add', 'remove'],
  },
];

const ACTIVE_CLS =
  'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:text-primary-foreground';
const INACTIVE_CLS = 'text-muted-foreground';

export function DrawingToolPalette({
  activeTool,
  hasSelection,
  onToolChange,
  onClearShapes,
  t,
}: DrawingToolPaletteProps) {
  const activeGroup = getGroup(activeTool);
  const activeMode = getMode(activeTool);
  const [expandedGroup, setExpandedGroup] = useState<ToolGroup | null>(activeGroup);

  useEffect(() => {
    if (activeTool === 'pan') setExpandedGroup(null);
  }, [activeTool]);

  useEffect(() => {
    // eslint-disable-next-line complexity -- keyboard shortcut handler has many cases by design
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      switch (e.key) {
        case 'v':
        case 'V':
          onToolChange('pan');
          break;
        case 'l':
          onToolChange('lasso-add');
          break;
        case 'L':
          onToolChange(e.shiftKey ? 'lasso-remove' : 'lasso-add');
          break;
        case 'r':
        case 'R':
          onToolChange('rectangle-add');
          break;
        case 'c':
        case 'C':
          onToolChange('circle-add');
          break;
        case 'p':
        case 'P':
          onToolChange('polygon-add');
          break;
        case 's':
        case 'S':
          onToolChange('point-add');
          break;
        case 'Escape':
          onToolChange('pan');
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onToolChange]);

  const handleGroupClick = (group: ToolGroup) => {
    if (group === expandedGroup) {
      setExpandedGroup(null);
    } else {
      setExpandedGroup(group);
      onToolChange(makeTool(group, group === activeGroup ? activeMode : 'add'));
    }
  };

  const handleSubMode = (group: ToolGroup, mode: ToolSubMode) => {
    onToolChange(makeTool(group, mode));
    setExpandedGroup(group);
  };

  const expandedRowIndex = expandedGroup
    ? 1 + EXPANDABLE_TOOLS.findIndex((tool) => tool.group === expandedGroup)
    : -1;

  const expandedToolDef = expandedGroup
    ? EXPANDABLE_TOOLS.find((tool) => tool.group === expandedGroup)
    : null;

  const visibleSubModes = expandedToolDef?.supportedModes
    ? SUB_MODES.filter((m) => expandedToolDef.supportedModes!.includes(m.mode))
    : SUB_MODES;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="absolute left-3 top-3 z-40">
        <div
          className="relative flex flex-col gap-1 rounded-xl border border-border bg-background/95 shadow-lg backdrop-blur-sm p-1"
          data-testid="draw-toolbar"
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                data-testid="tool-pan"
                aria-label={t('areaSelection.tools.pan')}
                aria-pressed={activeTool === 'pan'}
                onClick={() => {
                  onToolChange('pan');
                  setExpandedGroup(null);
                }}
                className={activeTool === 'pan' ? ACTIVE_CLS : INACTIVE_CLS}
              >
                <MousePointer2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={6}>
              {t('areaSelection.tools.pan')} ({t('areaSelection.tools.shortcut.pan')})
            </TooltipContent>
          </Tooltip>

          {EXPANDABLE_TOOLS.map(({ group, icon, labelKey, shortcutKey }) => (
            <Tooltip key={group}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  data-testid={`tool-${group}`}
                  aria-label={t(labelKey)}
                  aria-pressed={activeGroup === group}
                  aria-expanded={expandedGroup === group}
                  onClick={() => handleGroupClick(group)}
                  className={activeGroup === group ? ACTIVE_CLS : INACTIVE_CLS}
                >
                  {icon}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={6}>
                {t(labelKey)} ({t(shortcutKey)})
              </TooltipContent>
            </Tooltip>
          ))}

          <div className="my-0.5 h-px bg-border" />
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex" tabIndex={hasSelection ? -1 : 0}>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  data-testid="tool-clear-shapes"
                  aria-label={t('areaSelection.tools.clearShapes')}
                  disabled={!hasSelection}
                  onClick={onClearShapes}
                  className={
                    !hasSelection
                      ? 'cursor-not-allowed text-muted-foreground/50'
                      : 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={6}>
              {t('areaSelection.tools.clearShapes')}
            </TooltipContent>
          </Tooltip>
        </div>

        {expandedGroup !== null && expandedRowIndex >= 0 ? (
          <div
            className="absolute left-full ml-2 flex flex-row items-center gap-0.5 rounded-xl border border-border bg-background/95 px-1.5 py-1 shadow-lg backdrop-blur-sm z-50"
            style={{ top: subMenuTopPx(expandedRowIndex) }}
            data-testid={`submenu-${expandedGroup}`}
          >
            {visibleSubModes.map(({ mode, icon: subIcon, labelKey }) => {
              const isSubActive = activeGroup === expandedGroup && activeMode === mode;
              return (
                <Tooltip key={mode}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      data-testid={`tool-${expandedGroup}-${mode}`}
                      aria-label={t(labelKey)}
                      aria-pressed={isSubActive}
                      onClick={() => handleSubMode(expandedGroup, mode)}
                      className={
                        isSubActive ? ACTIVE_CLS : 'text-muted-foreground hover:text-foreground'
                      }
                    >
                      {subIcon}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" sideOffset={4}>
                    {t(labelKey)}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        ) : null}
      </div>
    </TooltipProvider>
  );
}
